/**
 * Field formatting utilities for custom attribute transformations.
 *
 * Provides functions to format field values before display, such as:
 * - Converting city codes to city names
 * - Rearranging address formats
 */

import cityLookup from "../config/cityLookup.json";

/**
 * Lookup table for city codes to city names, per-county (src/config/cityLookup.json).
 * Handles blank/empty values by treating them as "N/A".
 */
const CITY_LOOKUP: Record<string, string> = { " ": "N/A", ...cityLookup };

/**
 * Converts a city number code to the corresponding city name.
 * Returns "N/A" for blank, empty, null, or undefined values.
 * Returns the original value if no mapping is found.
 */
export function formatCityNum(cityNum: any): string {
  if (cityNum == null || cityNum === "" || cityNum === " ") {
    return "N/A";
  }

  const cityCode = String(cityNum).trim();
  return CITY_LOOKUP[cityCode] || cityNum;
}

/**
 * Formats a numeric value as US currency with comma separators (e.g. $100,000).
 * Returns the original value as string if it cannot be parsed as a number.
 */
export function formatCurrency(value: any): string {
  if (value == null || value === "") return "";
  const num = typeof value === "number" ? value : parseFloat(String(value).replace(/[,$]/g, ""));
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Rearranges an address string to move the street number to the front.
 * Example: "Main St 123" becomes "123 Main St"
 * Returns the original string if the last word is not a number.
 */
export function formatAddress(address: string | null | undefined): string {
  if (!address) return "";

  const parts = address.split(' ');
  if (parts.length < 2) return address;

  const streetNum = parts[parts.length - 1];

  // Check if the last part is a valid number
  if (!/^\d+$/.test(streetNum)) return address;

  const streetName = parts.slice(0, -1).join(' ');
  return `${streetNum} ${streetName}`;
}

/**
 * Main field formatting function that determines which formatter to apply
 * based on the field name.
 */
export function formatField(fieldName: string, fieldValue: any): any {
  switch (fieldName) {
    case "CITYNUM":
      return formatCityNum(fieldValue);
    case "ADDRESS":
      return formatAddress(fieldValue);
    case "APPRAISAL":
      return formatCurrency(fieldValue);
    default:
      return fieldValue;
  }
}
